import React, { useState, useEffect } from 'react';
import { 
  Shield, User, Building2, Upload, CheckCircle, 
  AlertCircle, ArrowLeft, FileText, CreditCard
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const KYCFormModern = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [accountType, setAccountType] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    mobile_number: '',
    email: '',
    pan_number: '',
    aadhaar_number: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });
  
  const [documents, setDocuments] = useState({
    pan_card: null,
    aadhaar_card: null,
    gst_certificate: null,
    msme_certificate: null
  });

  useEffect(() => {
    fetchKYCStatus();
    if (user) {
      setFormData(prev => ({
        ...prev,
        full_name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        mobile_number: user.phone || '',
        email: user.email || ''
      }));
      setAccountType(user.user_type || '');
    }
  }, [user]);

  const fetchKYCStatus = async () => {
    try {
      // Check cache first (5 minute TTL)
      const cached = localStorage.getItem('kyc_status_cache');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        if (age < 5 * 60 * 1000) { // 5 minutes
          setKycStatus(data);
          setLoading(false);
          // Still fetch in background to update cache
          fetchKYCStatusFromAPI();
          return;
        }
      }

      // If no cache or expired, fetch from API
      await fetchKYCStatusFromAPI();
    } catch (error) {
      console.error('Error fetching KYC status:', error);
      setLoading(false);
    }
  };

  const fetchKYCStatusFromAPI = async () => {
    try {
      const response = await api.get('/kyc/status');
      setKycStatus(response.data);
      
      // Cache the result
      localStorage.setItem('kyc_status_cache', JSON.stringify({
        data: response.data,
        timestamp: Date.now()
      }));
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching KYC status from API:', error);
      // If 404, user hasn't submitted KYC yet - this is normal
      if (error.response?.status === 404) {
        setKycStatus(null);
      }
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, file) => {
    if (file && file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "File size must be less than 5MB",
        variant: "destructive",
      });
      return;
    }
    setDocuments(prev => ({ ...prev, [field]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!accountType) {
      toast({
        title: "Account Type Required",
        description: "Please select your account type",
        variant: "destructive",
      });
      return;
    }

    // Validate documents based on account type
    if (accountType === 'business') {
      if (!documents.pan_card || !documents.gst_certificate) {
        toast({
          title: "Documents Required",
          description: "Please upload both Company PAN and GST Certificate",
          variant: "destructive",
        });
        return;
      }
    } else {
      if (!documents.pan_card || !documents.aadhaar_card) {
        toast({
          title: "Documents Required",
          description: "Please upload both PAN and Aadhaar documents",
          variant: "destructive",
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      // Upload documents based on account type
      let panResponse, aadhaarResponse, gstResponse;
      
      // PAN card is required for both types
      const panFormData = new FormData();
      panFormData.append('file', documents.pan_card);
      panFormData.append('document_type', accountType === 'business' ? 'company_pan' : 'pan_card');
      panResponse = await api.post('/kyc/upload-document', panFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (accountType === 'business') {
        // For business: upload GST certificate (required)
        const gstFormData = new FormData();
        gstFormData.append('file', documents.gst_certificate);
        gstFormData.append('document_type', 'gst_certificate');
        gstResponse = await api.post('/kyc/upload-document', gstFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Use PAN as authorized signatory ID for now
        aadhaarResponse = panResponse;
      } else {
        // For individual: upload Aadhaar (required)
        const aadhaarFormData = new FormData();
        aadhaarFormData.append('file', documents.aadhaar_card);
        aadhaarFormData.append('document_type', 'aadhaar_card');
        aadhaarResponse = await api.post('/kyc/upload-document', aadhaarFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      // Submit KYC data based on account type
      let kycData;
      
      if (accountType === 'business') {
        // Business KYC submission
        const gstFormData = new FormData();
        gstFormData.append('file', documents.gst_certificate);
        gstFormData.append('document_type', 'gst_certificate');
        
        const gstResponse = await api.post('/kyc/upload-document', gstFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        kycData = {
          user_type: 'business',
          business_data: {
            company_name: formData.full_name,
            gst_number: formData.pan_number,
            pan_number: formData.aadhaar_number,
            cin_number: '',
            registered_address: {
              street: formData.address,
              city: formData.city,
              state: formData.state,
              postal_code: formData.pincode,
              country: 'India'
            },
            authorized_person_name: formData.mobile_number,
            authorized_person_mobile: formData.email,
            bank_account_number: '',
            bank_ifsc_code: ''
          },
          business_documents: {
            gst_certificate_url: gstResponse.data.file_url,
            company_pan_url: panResponse.data.file_url,
            incorporation_certificate_url: '',
            cancelled_cheque_url: '',
            authorized_signatory_id_url: aadhaarResponse.data.file_url
          }
        };
      } else {
        // Individual KYC submission
        kycData = {
          user_type: 'individual',
          individual_data: {
            full_name: formData.full_name,
            date_of_birth: formData.date_of_birth,
            mobile_number: formData.mobile_number,
            email: formData.email,
            pan_number: formData.pan_number,
            aadhar_number: formData.aadhaar_number,
            address: {
              street: formData.address,
              city: formData.city,
              state: formData.state,
              postal_code: formData.pincode,
              country: 'India'
            }
          },
          individual_documents: {
            pan_card_url: panResponse.data.file_url,
            aadhar_front_url: aadhaarResponse.data.file_url,
            aadhar_back_url: aadhaarResponse.data.file_url
          }
        };
      }

      console.log('🔍 KYC SUBMISSION PAYLOAD:', JSON.stringify(kycData, null, 2));
      console.log('📋 Account Type:', accountType);
      console.log('📄 Payload Keys:', Object.keys(kycData));

      await api.post('/kyc/submit', kycData);

      // Clear cache
      localStorage.removeItem('kyc_status_cache');

      toast({
        title: "KYC Submitted Successfully",
        description: "Your KYC application is under review. We'll notify you once verified.",
      });

      // Redirect to profile
      setTimeout(() => {
        window.location.href = '/dashboard?tab=profile';
      }, 2000);

    } catch (error) {
      console.error('Error submitting KYC:', error);
      
      let errorMessage = "Failed to submit KYC. Please try again.";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle Pydantic validation errors
        if (Array.isArray(errorData.detail)) {
          const validationErrors = errorData.detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Submission Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-10 bg-gray-200 rounded w-40 mb-6"></div>
            <Card>
              <CardHeader className="bg-gray-100">
                <div className="h-8 bg-gray-200 rounded w-64"></div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="h-32 bg-gray-200 rounded"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
                <div className="h-48 bg-gray-200 rounded"></div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Show pending status message
  if (kycStatus?.status === 'pending' || kycStatus?.status === 'under_review') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/dashboard?tab=profile'}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Profile
          </Button>

          <Card className="border-orange-200">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-orange-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">KYC Under Review</h2>
                <p className="text-gray-600 mb-6">
                  Your KYC application is currently being reviewed by our team.
                  We'll notify you once the verification is complete.
                </p>
                <Badge className="bg-orange-100 text-orange-800 text-base px-4 py-2">
                  Status: Pending Review
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show verified status message
  if (kycStatus?.status === 'verified') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/dashboard?tab=profile'}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Profile
          </Button>

          <Card className="border-green-200">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">KYC Verified</h2>
                <p className="text-gray-600 mb-6">
                  Your KYC has been successfully verified. You now have access to all features.
                </p>
                <Badge className="bg-green-100 text-green-800 text-base px-4 py-2">
                  Status: Verified
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <Button
          variant="outline"
          onClick={() => window.location.href = '/dashboard?tab=profile'}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Profile
        </Button>

        <Card>
          <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100 border-b">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">Complete Your KYC</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Complete KYC to enable wallet usage. COD payments work without KYC.
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Account Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Select Account Type
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setAccountType('individual')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      accountType === 'individual'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <User className={`w-6 h-6 ${accountType === 'individual' ? 'text-orange-600' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <div className="font-semibold text-gray-900">Individual (B2C)</div>
                        <div className="text-xs text-gray-600">For personal use</div>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setAccountType('business')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      accountType === 'business'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className={`w-6 h-6 ${accountType === 'business' ? 'text-orange-600' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <div className="font-semibold text-gray-900">Business (B2B)</div>
                        <div className="text-xs text-gray-600">For companies</div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Personal/Business Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  {accountType === 'business' ? (
                    <>
                      <Building2 className="w-5 h-5 text-orange-600" />
                      Business Information
                    </>
                  ) : (
                    <>
                      <User className="w-5 h-5 text-orange-600" />
                      Personal Information
                    </>
                  )}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {accountType === 'business' ? (
                    <>
                      {/* Business Fields */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Registered Company Name *
                        </label>
                        <Input
                          value={formData.full_name}
                          onChange={(e) => handleInputChange('full_name', e.target.value)}
                          placeholder="As per GST Certificate"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          GST Number (GSTIN) *
                        </label>
                        <Input
                          value={formData.pan_number}
                          onChange={(e) => handleInputChange('pan_number', e.target.value.toUpperCase())}
                          placeholder="22AAAAA0000A1Z5"
                          maxLength={15}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Company PAN Number *
                        </label>
                        <Input
                          value={formData.aadhaar_number}
                          onChange={(e) => handleInputChange('aadhaar_number', e.target.value.toUpperCase())}
                          placeholder="AAAAA0000A"
                          maxLength={10}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Authorized Person Name *
                        </label>
                        <Input
                          value={formData.mobile_number}
                          onChange={(e) => handleInputChange('mobile_number', e.target.value)}
                          placeholder="Director/Partner/Proprietor Name"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contact Number *
                        </label>
                        <Input
                          type="tel"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="+91-XXXXXXXXXX"
                          required
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Individual Fields */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name (as per PAN/Aadhaar) *
                        </label>
                        <Input
                          value={formData.full_name}
                          onChange={(e) => handleInputChange('full_name', e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date of Birth *
                        </label>
                        <Input
                          type="date"
                          value={formData.date_of_birth}
                          onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Mobile Number *
                        </label>
                        <Input
                          type="tel"
                          value={formData.mobile_number}
                          onChange={(e) => handleInputChange('mobile_number', e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email ID *
                        </label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          PAN Number *
                        </label>
                        <Input
                          value={formData.pan_number}
                          onChange={(e) => handleInputChange('pan_number', e.target.value.toUpperCase())}
                          placeholder="ABCDE1234F"
                          maxLength={10}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Aadhaar Number *
                        </label>
                        <Input
                          value={formData.aadhaar_number}
                          onChange={(e) => handleInputChange('aadhaar_number', e.target.value.replace(/\D/g, ''))}
                          placeholder="1234 5678 9012"
                          maxLength={12}
                          required
                        />
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {accountType === 'business' ? 'Registered Office Address *' : 'Address *'}
                  </label>
                  <Input
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder={accountType === 'business' ? 'Building No., Street Name, Area' : ''}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State *
                    </label>
                    <Input
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pincode *
                    </label>
                    <Input
                      value={formData.pincode}
                      onChange={(e) => handleInputChange('pincode', e.target.value.replace(/\D/g, ''))}
                      maxLength={6}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Document Upload */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-orange-600" />
                  Document Upload
                </h3>

                {accountType === 'individual' ? (
                  /* Individual Documents: PAN + Aadhaar */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* PAN Card */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-orange-500 transition-colors">
                      <div className="text-center">
                        <CreditCard className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          PAN Card (PDF) *
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Upload both front and back in single PDF
                        </p>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handleFileChange('pan_card', e.target.files[0])}
                          className="hidden"
                          id="pan-upload"
                        />
                        <label
                          htmlFor="pan-upload"
                          className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 cursor-pointer text-sm"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose File
                        </label>
                        {documents.pan_card && (
                          <p className="text-xs text-green-600 mt-2">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            {documents.pan_card.name}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Aadhaar Card */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-orange-500 transition-colors">
                      <div className="text-center">
                        <CreditCard className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Aadhaar Card (PDF) *
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Upload both front and back in single PDF
                        </p>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handleFileChange('aadhaar_card', e.target.files[0])}
                          className="hidden"
                          id="aadhaar-upload"
                        />
                        <label
                          htmlFor="aadhaar-upload"
                          className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 cursor-pointer text-sm"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose File
                        </label>
                        {documents.aadhaar_card && (
                          <p className="text-xs text-green-600 mt-2">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            {documents.aadhaar_card.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : accountType === 'business' ? (
                  /* Business Documents: PAN + GST + MSME (optional) */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* PAN Card */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-orange-500 transition-colors">
                      <div className="text-center">
                        <CreditCard className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Company PAN Card (PDF) *
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Upload company PAN card
                        </p>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handleFileChange('pan_card', e.target.files[0])}
                          className="hidden"
                          id="pan-upload"
                        />
                        <label
                          htmlFor="pan-upload"
                          className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 cursor-pointer text-sm"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose File
                        </label>
                        {documents.pan_card && (
                          <p className="text-xs text-green-600 mt-2">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            {documents.pan_card.name}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* GST Certificate */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-orange-500 transition-colors">
                      <div className="text-center">
                        <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          GST Certificate (PDF) *
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Upload GST registration certificate
                        </p>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handleFileChange('gst_certificate', e.target.files[0])}
                          className="hidden"
                          id="gst-upload"
                        />
                        <label
                          htmlFor="gst-upload"
                          className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 cursor-pointer text-sm"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose File
                        </label>
                        {documents.gst_certificate && (
                          <p className="text-xs text-green-600 mt-2">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            {documents.gst_certificate.name}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* MSME Certificate (Optional) */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-orange-500 transition-colors md:col-span-2">
                      <div className="text-center">
                        <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          MSME Certificate (PDF) - Optional
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Upload MSME/Udyam registration certificate (if applicable)
                        </p>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handleFileChange('msme_certificate', e.target.files[0])}
                          className="hidden"
                          id="msme-upload"
                        />
                        <label
                          htmlFor="msme-upload"
                          className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 cursor-pointer text-sm"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose File
                        </label>
                        {documents.msme_certificate && (
                          <p className="text-xs text-green-600 mt-2">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            {documents.msme_certificate.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Please select an account type to continue
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Document Guidelines:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        {accountType === 'individual' ? (
                          <>
                            <li>Upload both front and back pages in a single PDF file</li>
                            <li>File size should not exceed 5MB per document</li>
                            <li>Documents should be clear and readable</li>
                            <li>Ensure all details are visible</li>
                          </>
                        ) : accountType === 'business' ? (
                          <>
                            <li>Upload clear scanned copies of all certificates</li>
                            <li>File size should not exceed 5MB per document</li>
                            <li>PAN and GST certificates are mandatory</li>
                            <li>MSME certificate is optional but recommended</li>
                            <li>Ensure all company details are clearly visible</li>
                          </>
                        ) : (
                          <li>Select account type to see document requirements</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.location.href = '/dashboard?tab=profile'}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                >
                  {submitting ? 'Submitting...' : 'Submit KYC Application'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KYCFormModern;
